import { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import { createSelector, StoreConfig } from '@ngrx/store';

import type { ConfigsMap, FeatureCode } from '@recruitee/feature-spec-types';
import { createSingleEntityStore, memoizeSelector, SingleEntityState } from '@recruitee/ngrx';

import { FEATURE_SPEC, FeatureSpecEntity, FeatureSpecMap } from './feature-spec.types';

const { actions, selectors, reducer, namespace } = createSingleEntityStore<FeatureSpecEntity>()(
  'FeatureSpec',
  {
    customActions: adapter => ({
      fetchFailed: (state, payload: { error: HttpErrorResponse | Error }) =>
        adapter.setError(state, payload.error.message),
    }),
  },
);

const FeatureSpecSelectors = {
  ...selectors,
  asMap: createSelector(selectors.data, data =>
    data !== null
      ? data.reduce((acc, feature) => ({ ...acc, [feature.codename]: feature }), <FeatureSpecMap>{})
      : null,
  ),
  forCode: memoizeSelector(<T extends FeatureCode>(code: T) =>
    createSelector(selectors.data, data => {
      const entity = data?.find(feature => feature.codename === code);
      if (!entity) {
        return undefined;
      }
      return <typeof entity & { configuration: ConfigsMap[typeof code] }>entity;
    }),
  ),
  isEnabled: memoizeSelector(<T extends FeatureCode>(code: T) =>
    createSelector(selectors.data, data => {
      const isEnabled = !!data?.find(feature => feature.codename === code)?.enabled;
      return isEnabled;
    }),
  ),
};

export const FeatureSpecStoreToken = new InjectionToken('FeatureSpecStoreToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<FeatureSpecEntity>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(FEATURE_SPEC),
    },
  }),
});

export {
  FeatureSpecSelectors,
  actions as FeatureSpecActions,
  reducer as FeatureSpecReducer,
  namespace as FeatureSpecNamespace,
};
